/**
 * RmdPanel — Required Minimum Distribution display.
 *
 * All numeric values (RMD factor, life expectancy, threshold ages) come from
 * `config.iraConfig` — NEVER hard-coded (feedback_no_hardcoded_rates).  The
 * Function is responsible for keeping that config up to date annually.
 */
import { useMemo } from 'react';
import { useConfig } from '@/contexts/ConfigContext';
import { KVPair, Pill } from '@/components/primitives';

export interface RmdPanelProps {
  planCode: string;             // 'TRAD' | 'INHERITED' | 'ROTH' …
  ownerBirthDate?: string;      // ISO-ish
  /** RMD year — defaults to current year. */
  year?: number;
  /** Prior-year-end balance used as the divisor basis. */
  priorYearEndBalance?: number;
}

function yearsBetween(iso?: string): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  return diff / (365.25 * 24 * 3600 * 1000);
}

export function RmdPanel({
  planCode,
  ownerBirthDate,
  year = new Date().getFullYear(),
  priorYearEndBalance,
}: RmdPanelProps): JSX.Element {
  const { config } = useConfig();

  // Index RMD factors by age.
  const rmdFactorsByAge = useMemo(() => {
    const map = new Map<number, number>();
    (config.iraConfig ?? []).forEach((row) => {
      if (row.configType === 'rmd-factor' && row.rmdFactor) {
        map.set(row.rmdFactor.age, row.rmdFactor.distributionPeriod);
      }
    });
    return map;
  }, [config.iraConfig]);

  const age = yearsBetween(ownerBirthDate);

  // SECURE Act 2.0 RMD start age — typed via the `rmd-start-age` variant on
  // `IraConfigRow` (shared-types). The Function seed carries one (or more)
  // rows with `configType === 'rmd-start-age'` and a numeric `startAge` +
  // optional `birthYearFrom`/`birthYearTo`. If the owner birth-date matches
  // a cohort row we use that; otherwise we fall back to the first active
  // rmd-start-age row. If nothing is seeded (route 503 / empty), the column
  // literal 73 is the SECURE-Act-2.0 default per memory/emily-ira-process.md.
  const rmdStartAgeRows = useMemo(() => {
    return (config.iraConfig ?? []).filter(
      (row) => row.configType === 'rmd-start-age' && row.rmdStartAge,
    );
  }, [config.iraConfig]);

  const ownerBirthYear = ownerBirthDate
    ? (() => {
        const y = new Date(ownerBirthDate).getFullYear();
        return Number.isFinite(y) ? y : null;
      })()
    : null;

  const rmdStartAge: number = useMemo(() => {
    if (rmdStartAgeRows.length === 0) return 73;
    if (ownerBirthYear !== null) {
      const match = rmdStartAgeRows.find((row) => {
        const cfg = row.rmdStartAge;
        if (!cfg) return false;
        const from = cfg.birthYearFrom ?? -Infinity;
        const to = cfg.birthYearTo ?? Infinity;
        return ownerBirthYear >= from && ownerBirthYear <= to;
      });
      if (match?.rmdStartAge) return match.rmdStartAge.startAge;
    }
    const first = rmdStartAgeRows[0]?.rmdStartAge;
    return first ? first.startAge : 73;
  }, [rmdStartAgeRows, ownerBirthYear]);

  const ageFloor = age !== null ? Math.floor(age) : null;
  const factor = ageFloor !== null ? (rmdFactorsByAge.get(ageFloor) ?? null) : null;

  const required = age !== null && age >= rmdStartAge;
  const estimated = required && factor && priorYearEndBalance
    ? priorYearEndBalance / factor
    : null;

  return (
    <div className="rim-rmd">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--pcb-blue-dark)' }}>
          Required Minimum Distribution · {year}
        </h3>
        {required ? <Pill variant="warn">RMD required</Pill> : <Pill variant="info">Not yet required</Pill>}
      </div>

      {planCode === 'ROTH' && (
        <div className="rim-rmd-banner">
          Roth IRAs do NOT require RMDs for the original owner.  RMD rules apply to Inherited Roth.
        </div>
      )}

      {planCode === 'INHERITED' && (
        <div className="rim-rmd-banner is-warn">
          Inherited IRA — distribution rules depend on beneficiary status (eligible designated vs. non-DB vs. non-person).  See Publication 590-B.
        </div>
      )}

      <div className="rim-kv-grid">
        <KVPair label="Plan type" value={planCode} />
        <KVPair label="RMD start age (config)" value={rmdStartAge} />
        <KVPair label="Owner age" value={age !== null ? age.toFixed(1) : '—'} />
        <KVPair label="Prior YE balance" value={priorYearEndBalance !== undefined ? priorYearEndBalance.toFixed(2) : '—'} />
        <KVPair label="Life expectancy factor" value={factor !== null ? factor.toFixed(2) : ageFloor !== null ? '(config missing)' : '—'} />
        <KVPair label="Estimated RMD" value={estimated !== null ? `$${estimated.toFixed(2)}` : '—'} />
      </div>

      {factor === null && required && ageFloor !== null && (
        <div className="rim-rmd-banner is-warn">
          No RMD factor is set for age {ageFloor} in ao_IraConfig — the Function team must load this year&apos;s
          Uniform Lifetime factors before automatic RMD display will populate.
        </div>
      )}
    </div>
  );
}
